/**
 * Packers line weights (trade salmon / whole-muscle style lines).
 * One weightKg per order line SKU for the whole quantity (not piece-by-piece).
 * Merge-only onto orderPacking; never blocks Mark Packed.
 */

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import unifiedProductService from './unifiedProductService';

function sanitizeDocumentId(id) {
    const value = (id ?? '').toString().trim();
    if (!value) return 'unknown-order';
    return value.replace(/[\/\\.#\[\]]+/g, '--');
}

function removeUndefined(obj) {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) {
        return obj.map(removeUndefined).filter((x) => x !== null && x !== undefined);
    }
    if (typeof obj === 'object' && !(obj instanceof Timestamp) && !(obj instanceof Date)) {
        const out = {};
        Object.keys(obj).forEach((key) => {
            const v = removeUndefined(obj[key]);
            if (v !== undefined) out[key] = v;
        });
        return out;
    }
    return obj;
}

export function packingLineWeightKey(sku) {
    return String(sku || '').trim().toUpperCase();
}

/**
 * Format kg for Packers UI, storage maps, reports, and packing slips.
 * Never round or truncate user/scale kg. (Grams→kg cut is only via njordScaleGramsToKg.)
 * @param {number|string|null|undefined} value
 * @returns {string} empty string when missing/invalid
 */
export function formatPackWeightKg(value) {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string') {
        const t = value.trim();
        if (t !== '' && Number.isFinite(Number(t))) return t;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return String(n);
}

/**
 * @param {Array<object>|null|undefined} list
 * @param {string} sku
 */
export function findProductLineWeight(list, sku) {
    const key = packingLineWeightKey(sku);
    if (!key) return null;
    return (
        (Array.isArray(list) ? list : []).find(
            (e) => packingLineWeightKey(e?.sku) === key
        ) || null
    );
}

/**
 * @param {Array<object>|null|undefined} list
 * @returns {Record<string, number>}
 */
export function recordedWeightsMapFromList(list) {
    const map = {};
    for (const entry of Array.isArray(list) ? list : []) {
        const sku = String(entry?.sku || '').trim();
        const w = Number(entry?.weightKg);
        if (sku && Number.isFinite(w) && w > 0) {
            map[sku] = w;
        }
    }
    return map;
}

/**
 * Prefer productLineWeights[], then legacy recordedWeights map on the packing doc.
 * @param {object|null|undefined} packingRecord
 * @returns {Record<string, number>}
 */
export function resolveRecordedWeightsMap(packingRecord) {
    if (!packingRecord) return {};
    const fromList = recordedWeightsMapFromList(packingRecord.productLineWeights);
    if (Object.keys(fromList).length > 0) return fromList;
    const legacy = packingRecord.recordedWeights;
    if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
        const map = {};
        Object.entries(legacy).forEach(([sku, value]) => {
            const w = Number(value);
            if (sku && Number.isFinite(w) && w > 0) {
                map[String(sku).trim()] = w;
            }
        });
        return map;
    }
    return {};
}

/**
 * Upsert one line weight on orderPacking (creates stub if missing).
 * @param {string} orderId
 * @param {{ sku: string, weightKg: number, quantity?: number|null }} payload
 * @param {object} user
 */
export async function upsertProductLineWeight(orderId, payload, user) {
    const sku = String(payload?.sku || '').trim();
    const weightKg = Number(payload?.weightKg);
    if (!orderId || !sku) {
        throw new Error('Order and SKU are required.');
    }
    if (!Number.isFinite(weightKg) || weightKg <= 0) {
        throw new Error('Enter a valid weight greater than zero (kg).');
    }

    const safeOrderId = sanitizeDocumentId(orderId);
    const packingRef = doc(db, 'orderPacking', safeOrderId);
    const snap = await getDoc(packingRef);
    const data = snap.exists() ? snap.data() : {};
    const existingList = Array.isArray(data.productLineWeights) ? [...data.productLineWeights] : [];
    const key = packingLineWeightKey(sku);
    const recordedBy = user?.email || user?.displayName || 'unknown';

    let productName = sku;
    try {
        productName = unifiedProductService.getProductName(sku) || sku;
    } catch {
        productName = sku;
    }

    const qty =
        payload.quantity != null && Number.isFinite(Number(payload.quantity))
            ? Number(payload.quantity)
            : null;

    const entry = removeUndefined({
        sku,
        productName,
        quantity: qty,
        weightKg,
        recordedAt: Timestamp.now(),
        recordedBy,
        source: 'live',
    });

    const next = existingList.filter((e) => packingLineWeightKey(e?.sku) !== key);
    next.push(entry);

    const weightMap = {
        ...(typeof data.recordedWeights === 'object' && data.recordedWeights && !Array.isArray(data.recordedWeights)
            ? data.recordedWeights
            : {}),
        ...recordedWeightsMapFromList(next),
    };

    const writePayload = removeUndefined({
        orderId: data.orderId || orderId,
        productLineWeights: next,
        recordedWeights: weightMap,
        updatedAt: Timestamp.now(),
        ...(!snap.exists()
            ? {
                  createdAt: Timestamp.now(),
                  packedBy: recordedBy,
                  batchAllocations: data.batchAllocations || [],
              }
            : {}),
    });

    await setDoc(packingRef, writePayload, { merge: true });
    return entry;
}

/**
 * Merge a weight map onto orderPacking at pack time (optional weights).
 * @param {string} orderId
 * @param {Record<string, number|string>} weightMap
 * @param {object} user
 * @param {Array<{sku?: string, quantity?: number}>} [orderItems]
 */
export async function mergeRecordedWeightsOntoPacking(orderId, weightMap, user, orderItems = []) {
    const cleaned = {};
    Object.entries(weightMap || {}).forEach(([sku, value]) => {
        const w = Number(value);
        if (sku && Number.isFinite(w) && w > 0) {
            cleaned[String(sku).trim()] = w;
        }
    });
    if (!orderId || Object.keys(cleaned).length === 0) {
        return { persisted: false };
    }

    const qtyBySku = {};
    for (const item of orderItems || []) {
        const sku = String(item?.sku || item?.code || '').trim();
        if (!sku) continue;
        const q = Number(item?.quantity ?? item?.qty ?? 1) || 1;
        qtyBySku[sku] = (qtyBySku[sku] || 0) + q;
    }

    for (const [sku, weightKg] of Object.entries(cleaned)) {
        await upsertProductLineWeight(
            orderId,
            { sku, weightKg, quantity: qtyBySku[sku] ?? null },
            user
        );
    }
    return { persisted: true };
}

/**
 * Attach line weight (per SKU on the order) onto flattened batch trail rows.
 * Same weight repeats on every batch row for that SKU (line total, not per piece).
 * @param {Array<object>} batchRows
 * @param {Array<object>} packingRecords
 */
export function attachLineWeightsToBatchLines(batchRows = [], packingRecords = []) {
    const byOrder = new Map();
    for (const rec of packingRecords || []) {
        const id = String(rec.orderId || rec.id || '');
        if (id) byOrder.set(id, rec);
        if (rec.id) byOrder.set(String(rec.id), rec);
    }

    return (batchRows || []).map((row) => {
        const rec =
            byOrder.get(String(row.orderId || '')) ||
            byOrder.get(String(row.orderNumber || '')) ||
            null;
        const map = resolveRecordedWeightsMap(rec);
        const sku = String(row.sku || '').trim();
        const weightKg = sku && map[sku] != null ? map[sku] : null;
        const entry = findProductLineWeight(rec?.productLineWeights, sku);
        return {
            ...row,
            weightKg,
            weightRecordedBy: entry?.recordedBy || null,
            lineQuantityForWeight: entry?.quantity ?? null,
        };
    });
}
